import React, { useEffect, useRef } from 'react';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function CheckoutModal({
  abierto,
  mediosPago,
  medioSeleccionado,
  dividir,
  medioSeleccionado2,
  monto1,
  monto2,
  password,
  procesando,
  error,
  total,
  onSelect,
  onToggleDividir,
  onSelect2,
  onMonto1Change,
  onMonto2Change,
  onPasswordChange,
  onConfirm,
  onClose,
  onSelectByIndex,
}) {
  const modalRef = useRef(null);
  const pinRef = useRef(null);
  const montoRef = useRef(null);
  const monto2Ref = useRef(null);

  const mediosRef = useRef(mediosPago);
  const medioSeleccionadoRef = useRef(medioSeleccionado);
  const onCloseRef = useRef(onClose);
  const onConfirmRef = useRef(onConfirm);
  const onSelectByIndexRef = useRef(onSelectByIndex);

  mediosRef.current = mediosPago;
  medioSeleccionadoRef.current = medioSeleccionado;
  onCloseRef.current = onClose;
  onConfirmRef.current = onConfirm;
  onSelectByIndexRef.current = onSelectByIndex;

  useEffect(() => {
    if (!abierto) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      modalRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      const modal = modalRef.current;
      const pin = pinRef.current;

      if (!modal) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      // El PIN y el monto del segundo medio son campos de texto: ahí las
      // flechas y Enter no eligen medio de pago.
      const estaEnPin =
        document.activeElement === pin ||
        document.activeElement === montoRef.current ||
        document.activeElement === monto2Ref.current;

      if (
        !estaEnPin &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp')
      ) {
        event.preventDefault();
        event.stopPropagation();

        const medios = mediosRef.current || [];

        if (!medios.length) return;

        const actualIndex = medios.findIndex(
          (medio) =>
            medio.id === medioSeleccionadoRef.current
        );

        const baseIndex = actualIndex >= 0 ? actualIndex : 0;

        const siguienteIndex =
          event.key === 'ArrowDown'
            ? (baseIndex + 1) % medios.length
            : (baseIndex - 1 + medios.length) % medios.length;

        onSelectByIndexRef.current?.(siguienteIndex);
        return;
      }

      if (
        !estaEnPin &&
        ['PageUp', 'PageDown', 'Home', 'End'].includes(event.key)
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();

        if (document.activeElement !== pin) {
          pin?.focus();
          return;
        }

        onConfirmRef.current?.();
        return;
      }

      if (event.key === 'Tab') {
        const focusables = Array.from(
          modal.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        );

        if (!focusables.length) {
          event.preventDefault();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
          return;
        }

        if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );

      document.body.style.overflow = previousOverflow;
    };
  }, [abierto]);

  // Al dividir, el cursor va directo al monto del primer medio.
  useEffect(() => {
    if (abierto && dividir) {
      requestAnimationFrame(() => montoRef.current?.focus());
    }
  }, [abierto, dividir]);

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4 backdrop-blur-sm">
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cantina-checkout-title"
        className={`flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none ${
          dividir ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        <div
          className={`shrink-0 bg-gradient-to-r from-azul-oscuro to-azul-lago px-6 text-white ${
            dividir ? 'py-3' : 'py-5'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amarillo-dorado">
            Cantina
          </div>

          <div className="mt-1 flex items-end justify-between gap-4">
            <div>
              <h2
                id="cantina-checkout-title"
                className="text-2xl font-bold"
              >
                Cobrar venta
              </h2>

              <p className="mt-1 text-sm text-white/75">
                Elegí el medio con ↑ ↓ y presioná Enter.
              </p>
            </div>

            <strong className="text-3xl">
              {money(total)}
            </strong>
          </div>
        </div>

        <div
          className={`min-h-0 flex-1 overflow-y-auto ${
            dividir ? 'p-4 md:p-5' : 'p-6'
          }`}
        >
          <div
            className={
              dividir ? 'grid gap-6 md:grid-cols-2' : ''
            }
          >
            <div>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {dividir ? 'Primer medio de pago' : 'Medio de pago'}
                </p>

                <button
                  type="button"
                  onClick={onToggleDividir}
                  disabled={procesando}
                  className="text-xs font-bold text-azul-lago underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {dividir ? 'Usar un solo medio' : 'Dividir en dos medios'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {mediosPago.map((medio) => {
                  const selected = medio.id === medioSeleccionado;

                  return (
                    <button
                      key={medio.id}
                      type="button"
                      onClick={() => onSelect(medio.id)}
                      className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition ${
                        selected
                          ? 'border-verde-esmeralda bg-verde-esmeralda text-white'
                          : 'border-slate-200 bg-white text-azul-oscuro hover:border-azul-lago'
                      }`}
                    >
                      <span className="font-semibold">{medio.nombre}</span>

                      <span
                        className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${
                          selected
                            ? 'bg-white/20 text-white'
                            : 'text-transparent'
                        }`}
                      >
                        ✓
                      </span>
                    </button>
                  );
                })}
              </div>

              {dividir && (
                <>
                  <label
                    htmlFor="cantina-monto-1"
                    className="mb-2 mt-4 block text-xs font-bold uppercase tracking-wider text-slate-500"
                  >
                    Monto con el primer medio
                  </label>

                  <input
                    id="cantina-monto-1"
                    ref={montoRef}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={monto1}
                    onChange={(event) => onMonto1Change(event.target.value)}
                    placeholder={`Total ${money(total)}: cuánto se cobra con este medio`}
                    disabled={procesando}
                    className="h-12 w-full rounded-xl border-2 border-slate-300 px-4 text-lg outline-none transition focus:border-verde-esmeralda focus:ring-4 focus:ring-verde-esmeralda/20"
                  />
                </>
              )}
            </div>

            {dividir && (
              <div>
                <div className="mb-3 flex min-h-[1rem] items-center">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Segundo medio de pago
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {mediosPago
                    .filter((medio) => medio.id !== medioSeleccionado)
                    .map((medio) => {
                      const selected = medio.id === medioSeleccionado2;

                      return (
                        <button
                          key={medio.id}
                          type="button"
                          onClick={() => onSelect2(medio.id)}
                          className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition ${
                            selected
                              ? 'border-verde-esmeralda bg-verde-esmeralda text-white'
                              : 'border-slate-200 bg-white text-azul-oscuro hover:border-azul-lago'
                          }`}
                        >
                          <span className="font-semibold">{medio.nombre}</span>

                          <span
                            className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${
                              selected
                                ? 'bg-white/20 text-white'
                                : 'text-transparent'
                            }`}
                          >
                            ✓
                          </span>
                        </button>
                      );
                    })}
                </div>

                <label
                  htmlFor="cantina-monto-2"
                  className="mb-2 mt-4 block text-xs font-bold uppercase tracking-wider text-slate-500"
                >
                  Monto con el segundo medio
                </label>

                <input
                  id="cantina-monto-2"
                  ref={monto2Ref}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={monto2}
                  onChange={(event) => onMonto2Change(event.target.value)}
                  placeholder="Se completa solo con lo que resta"
                  disabled={procesando}
                  className="h-12 w-full rounded-xl border-2 border-slate-300 bg-slate-50 px-4 text-lg outline-none transition focus:border-verde-esmeralda focus:ring-4 focus:ring-verde-esmeralda/20"
                />
              </div>
            )}
          </div>

          <div
            className={
              dividir
                ? 'mt-4 md:flex md:items-end md:gap-4'
                : ''
            }
          >
          <div className={dividir ? 'md:flex-1' : 'mt-6'}>
            <label
              htmlFor="cantina-pin"
              className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500"
            >
              PIN del vendedor
            </label>

            <input
              id="cantina-pin"
              ref={pinRef}
              name="cantina-operator-pin"
              type="password"
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              value={password}
              onChange={(event) =>
                onPasswordChange(event.target.value)
              }
              placeholder="Ingresá tu PIN"
              disabled={procesando}
              className="h-12 w-full rounded-xl border-2 border-slate-300 px-4 text-lg outline-none transition focus:border-verde-esmeralda focus:ring-4 focus:ring-verde-esmeralda/20"
            />

            {error && (
              <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                {error}
              </div>
            )}
          </div>

          <div
            className={`flex gap-3 ${
              dividir ? 'mt-4 md:mt-0 md:w-96' : 'mt-6'
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={procesando}
              className="h-11 flex-1 rounded-xl border border-slate-300 bg-white font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
              <span className="ml-2 text-[10px] text-slate-400">
                Esc
              </span>
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={procesando}
              className="h-11 flex-[1.4] rounded-xl bg-verde-esmeralda font-bold text-white shadow-md transition hover:bg-verde-oscuro disabled:opacity-50"
            >
              {procesando
                ? 'Procesando...'
                : 'Confirmar venta'}

              {!procesando && (
                <span className="ml-2 text-[10px] text-white/70">
                  Enter
                </span>
              )}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
